import type { ComponentType } from 'react';

import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';

import { SOCKET_POC_FAILED, SOCKET_POC_PUSHED, SOCKET_POC_SUCCESS } from '../../config/constants';
import { getRequest, patchRequest, postRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { FeatureFetchStatus, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

import {
  type PocState,
  type PocInitiationRes,
  type FreshPocSessionData,
  type PocFailureSocketData,
  type PocSessionSituation,
  type SessionRequiringDisplay,
  type InteractiveSessionPayload,
  type PocSuccessNotice,
} from './types';

export class PocStore implements IFeatureManagerStore<'PushPoc'> {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    const pushPocHandler = this.handlePushedPoc.bind(this);
    const pocFailureHandler = this.handlePocFailure.bind(this);
    const pocSuccessHandler = this.handlePocSuccess.bind(this);

    autorun(() => {
      if (this.rootStore.authStore.authorized) {
        socket.on(SOCKET_POC_PUSHED, pushPocHandler);
        socket.on(SOCKET_POC_FAILED, pocFailureHandler);
        socket.on(SOCKET_POC_SUCCESS, pocSuccessHandler);
      } else {
        socket.off(SOCKET_POC_PUSHED, pushPocHandler);
        socket.off(SOCKET_POC_FAILED, pocFailureHandler);
        socket.off(SOCKET_POC_SUCCESS, pocSuccessHandler);
        this.cleanupPocState();
      }
    });

    reaction(
      () => this.rootStore.payRedeemStore.redemptionCaptureResult,
      (redemptionCaptureResult) => {
        if (!redemptionCaptureResult) {
          return;
        }

        const { preparationResult, redemptionRequestId, redemptionAmount } = redemptionCaptureResult;

        if (preparationResult?.status !== 'validityIssue') {
          return;
        }

        const { status, payload } = preparationResult.payload;

        switch (status) {
          case 'pocRequired':
            this.setPocState({
              step: 'init',
              flow: 'redemption',
              commonData: {
                cardNumberEnding: payload.cardNumberEnding,
                requestReason: payload.requestReason,
                attemptNumber: 0,
              },
              coreData: {
                situation: 'required',
                autoInitReasons: payload.reasons,
                paymentTokenId: payload.paymentTokenId,
              },
              extraData: {
                redemptionRequestId,
                redemptionAmount,
              },
            });

            break;
          case 'pocInProgress':
            if (payload.hangingData) {
              this.setPocState({
                step: 'init',
                flow: 'redemption',
                commonData: {
                  cardNumberEnding: payload.hangingData.cardNumberEnding,
                  requestReason: payload.hangingData.requestReason,
                  attemptNumber: payload.hangingData.attemptNumber,
                },
                coreData: { situation: 'hanging', hangingSessionId: payload.pocSessionId },
                extraData: {
                  redemptionRequestId,
                  redemptionAmount,
                },
              });
            }

            break;
          case 'pocFailed':
            if (this.pocState?.flow !== 'hanging') {
              const nextState: PocState =
                payload.failureType === 'retry'
                  ? {
                      step: 'init',
                      flow: 'redemption',
                      commonData: {
                        cardNumberEnding: payload.cardNumberEnding,
                        requestReason: payload.requestReason,
                        attemptNumber: payload.attemptNumber,
                      },
                      coreData: {
                        situation: 'failed',
                        retriedPocSessionId: payload.pocSessionId,
                        failureType: payload.failureType,
                      },
                      extraData: {
                        redemptionRequestId,
                        redemptionAmount,
                      },
                    }
                  : {
                      step: 'init',
                      flow: 'failed',
                      commonData: {
                        cardNumberEnding: payload.cardNumberEnding,
                        requestReason: payload.requestReason,
                        attemptNumber: payload.attemptNumber,
                      },
                      coreData: {
                        retriedPocSessionId: payload.pocSessionId,
                      },
                      extraData: {
                        failureType: payload.failureType,
                        redemptionAmount,
                      },
                    };

              this.setPocState(nextState);
            }

            break;
          default:
        }
      },
    );
  }

  @observable public pocState: PocState | null = null;

  @observable private userIsPerformingPoc: boolean = false;

  public dialogTypes = ['PushPoc'] as const;
  public PocReactionComponent: ComponentType | null = null;
  public PocSuccessNtfComponent: ComponentType | null = null;

  private interactivePocSessionRequest: Promise<boolean> | null = null;
  private hangingSessionUnseen: boolean = false;

  private _successNotice: PocSuccessNotice | null = null;

  public get successNotice() {
    return this._successNotice;
  }

  private set successNotice(value) {
    this._successNotice = value;
  }

  @computed
  private get pocSessionSituation(): PocSessionSituation {
    if (this.pocState == null || this.pocState.flow === 'redemption') {
      return 'notHanging';
    }

    if (this.pocState.flow === 'failed' && this.pocState.extraData.failureType !== 'retry') {
      return 'hangingNoRetry';
    }

    return 'hangingAllowsRetry';
  }

  @computed
  private get pocSessionIsHanging() {
    return this.pocSessionSituation !== 'notHanging';
  }

  @computed
  public get shouldShowHangingSessionMarker() {
    return !this.userIsPerformingPoc && this.pocSessionIsHanging;
  }

  @computed
  public get shouldShowOperatePocButton() {
    return !this.userIsPerformingPoc && this.pocSessionSituation === 'hangingAllowsRetry';
  }

  @action
  public setPocState(pocState: PocState) {
    this.pocState = pocState;
  }

  @action
  public setUserIsPerformingPoc(isPerforming: boolean) {
    this.userIsPerformingPoc = isPerforming;
  }

  private handleInteractiveHanging(payload: InteractiveSessionPayload<'hanging'>) {
    const { id, failureNoticed, status, paymentToken, seenByUser, attemptNumber } = payload.hangingPocSession;

    const isFailed = status !== 'init';

    this.setPocState({
      step: 'init',
      commonData: {
        cardNumberEnding: paymentToken.lastFour || '????',
        attemptNumber,
        requestReason: payload.requestReason,
      },
      ...(isFailed
        ? {
            flow: 'failed',
            coreData: { retriedPocSessionId: id },
            extraData: { failureType: status },
          }
        : {
            flow: 'hanging',
            coreData: { hangingSessionId: id },
            extraData: {},
          }),
    });

    this.hangingSessionUnseen = (isFailed && !failureNoticed) || (!isFailed && !seenByUser);

    return true;
  }

  private handleInteractiveSuccess(payload: InteractiveSessionPayload<'unseenSuccess'>) {
    this.successNotice = {
      pocSessionId: payload.successfulPocSession.id,
      cardNumberEnding: payload.successfulPocSession.paymentToken.lastFour || '????',
    };

    return true;
  }

  private fetchInteractivePocSession() {
    const pocEnabled = resolveFlag('poc-enabled', true);

    if (!pocEnabled) {
      return;
    }

    if (this.interactivePocSessionRequest) {
      return this.interactivePocSessionRequest;
    }

    const req = getRequest<SessionRequiringDisplay>('/v2/poc/session-requiring-display')
      .then((res) => {
        if (!this.rootStore.authStore.authorized || !this.interactivePocSessionRequest) {
          return false;
        }

        switch (res.data.status) {
          case 'hanging':
            return this.handleInteractiveHanging(res.data.payload);
          case 'unseenSuccess':
            return this.handleInteractiveSuccess(res.data.payload);
          case 'noSessions':
            return false;
        }
      })
      .catch((error) => {
        logToNewRelic(
          'POC-FE - failed to fetch hanging POC session',
          `POC-FE - failed to fetch hanging POC session: ${JSON.stringify(error)}`,
          'error',
        );

        return false;
      })
      .finally(() => {
        this.interactivePocSessionRequest = null;
      });

    this.interactivePocSessionRequest = req;

    return req;
  }

  async fetch(): Promise<FeatureFetchStatus> {
    await this.fetchInteractivePocSession();

    return 'READY';
  }

  startDialogs() {
    if (this.PocReactionComponent && this.hangingSessionUnseen && this.pocSessionIsHanging) {
      this.rootStore.portalStore.openPortal(this.PocReactionComponent, {
        beforeClose: () => this.rootStore.featureManagerStore.onDialogFinished('PushPoc'),
      });

      return true;
    }

    if (this.PocSuccessNtfComponent && this.successNotice) {
      this.rootStore.portalStore.openPortal(this.PocSuccessNtfComponent, {
        beforeClose: () => this.rootStore.featureManagerStore.onDialogFinished('PushPoc'),
      });
    }

    return false;
  }

  public async noticePocSuccess() {
    if (!this.successNotice) {
      logToNewRelic(
        'POC-FE - attempt to mark POC success seen while there is no successful POC up',
        'POC-FE - attempt to mark POC success while there is no successful POC up',
        'error',
      );

      return false;
    }

    try {
      const res = await patchRequest<{ data: { marked: boolean } }>('/v2/poc/mark-success-noticed', {
        pocSessionId: this.successNotice.pocSessionId,
      });

      if (res.data.marked) {
        this.successNotice = null;
      }

      return res.data.marked;
    } catch {
      return false;
    }
  }

  public async noticeCurrentPocSession() {
    if (
      !this.pocState ||
      this.pocState.step !== 'init' ||
      (this.pocState.flow !== 'hanging' && this.pocState.flow !== 'failed')
    ) {
      logToNewRelic(
        'POC-FE - attempt to mark POC seen while there is no hanging POC up',
        'POC-FE - attempt to mark POC seen while there is no hanging POC up',
        'error',
      );

      return false;
    }

    try {
      const { flow, coreData } = this.pocState;
      const isFailed = flow === 'failed';
      const endpoint = isFailed ? '/v2/poc/mark-failure-noticed' : '/v2/poc/mark-as-seen';
      const pocSessionId = isFailed ? coreData.retriedPocSessionId : coreData.hangingSessionId;

      const res = await patchRequest<{ data: { marked: boolean } }>(endpoint, {
        pocSessionId,
      });

      if (res.data.marked) {
        this.hangingSessionUnseen = false;
      }

      return res.data.marked;
    } catch {
      return false;
    }
  }

  private static async fetchAccessToken(pocSessionId: string) {
    const res = await getRequest<{ data: { accessToken: string } }>('/v2/poc/access-token', {
      pocSessionId,
    });

    return res.data.accessToken;
  }

  public async initPocSession(relatedRedemptionAmount?: number | null) {
    if (this.interactivePocSessionRequest) {
      await this.interactivePocSessionRequest;
    }

    if (this.pocState == null || this.pocState.step !== 'init') {
      logToNewRelic(
        'POC-FE - init session - no data to base new session on',
        'POC-FE - init session - no data to base new session on',
        'error',
      );

      return false;
    }

    if ('hangingSessionId' in this.pocState.coreData) {
      const accessToken = await PocStore.fetchAccessToken(this.pocState.coreData.hangingSessionId);

      this.setPocState({
        flow: 'hanging',
        step: 'process',
        commonData: this.pocState.commonData,
        coreData: {
          accessToken,
          pocSessionId: this.pocState.coreData.hangingSessionId,
        },
        extraData: { redemptionAmount: relatedRedemptionAmount },
      });

      return true;
    }

    try {
      const { data } = await postRequest<PocInitiationRes>('/v2/poc/init-session', {
        ...this.pocState.coreData,
        version: 2,
        redemptionRequestId:
          this.pocState.flow === 'redemption' ? this.pocState.extraData.redemptionRequestId : undefined,
      });

      if (!data.result) {
        logToNewRelic('POC-FE - init session', `POC-FE - init session - ${data.problem}`, 'error');

        return false;
      }

      this.setPocState({
        ...this.pocState,
        step: 'process',
        coreData: {
          pocSessionId: data.result.pocSession.id,
          accessToken: data.result.accessToken,
        },
      });

      return true;
    } catch (error) {
      logToNewRelic('POC-FE - init session', `POC-FE - init session - ${JSON.stringify(error)}`, 'error');

      return false;
    }
  }

  public async acquireAccessToken(): Promise<string> {
    if (this.pocState == null || this.pocState.step !== 'process') {
      logToNewRelic(
        'POC-FE - acquire token - no session in process',
        'POC-FE - acquire token - no session in process',
        'error',
      );

      return '';
    }

    try {
      const accessToken = await PocStore.fetchAccessToken(this.pocState.coreData.pocSessionId);

      this.setPocState({
        ...this.pocState,
        coreData: {
          ...this.pocState.coreData,
          accessToken,
        },
      });

      return accessToken;
    } catch (error) {
      logToNewRelic('POC-FE - acquire token', `POC-FE - acquire token - ${JSON.stringify(error)}`, 'error');

      return '';
    }
  }

  private getHungState(): null | PocState {
    if (!this.pocState) {
      return null;
    }

    if (this.pocState.step === 'process') {
      return {
        step: 'init',
        flow: 'hanging',
        coreData: { hangingSessionId: this.pocState.coreData.pocSessionId },
        commonData: this.pocState.commonData,
        extraData: {},
      };
    }

    if (this.pocState.flow !== 'redemption') {
      return null;
    }

    switch (this.pocState.coreData.situation) {
      case 'required':
        // Session is not started yet, nothing to hang
        return null;
      case 'failed':
        return {
          step: 'init',
          flow: 'failed',
          coreData: { retriedPocSessionId: this.pocState.coreData.retriedPocSessionId },
          commonData: this.pocState.commonData,
          extraData: { failureType: this.pocState.coreData.failureType },
        };
      case 'hanging':
        return {
          step: 'init',
          flow: 'hanging',
          coreData: { hangingSessionId: this.pocState.coreData.hangingSessionId },
          commonData: this.pocState.commonData,
          extraData: {},
        };
    }
  }

  @action
  public hangCurrentPocSession() {
    const hungState = this.getHungState();

    if (hungState) {
      this.setPocState(hungState);
    }
  }

  @action
  public cleanupPocState() {
    this.pocState = null;
    this.interactivePocSessionRequest = null;
    this.userIsPerformingPoc = false;
    this.hangingSessionUnseen = false;
    this.successNotice = null;
  }

  private static isPushPocData(socketData: unknown): socketData is FreshPocSessionData {
    return (
      !!socketData &&
      typeof socketData === 'object' &&
      'cardNumberEnding' in socketData &&
      'pocSession' in socketData &&
      !!socketData.pocSession &&
      typeof socketData.pocSession === 'object' &&
      'id' in socketData.pocSession
    );
  }

  private handlePushedPoc(socketData: { data: unknown }) {
    if (!PocStore.isPushPocData(socketData.data)) {
      logToNewRelic(
        'POC-FE - received unexpected data from socket on POC_PUSHED event',
        `POC-FE - received unexpected data from socket on POC_PUSHED event: ${JSON.stringify(socketData.data)}`,
        'error',
      );

      return;
    }

    this.setPocState({
      step: 'init',
      flow: 'hanging',
      commonData: { cardNumberEnding: socketData.data.cardNumberEnding, requestReason: 'manual', attemptNumber: 0 },
      coreData: { hangingSessionId: socketData.data.pocSession.id },
      extraData: {},
    });
    this.hangingSessionUnseen = true;

    this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['PushPoc']);
  }

  private static isPocFailedData(socketData: unknown): socketData is PocFailureSocketData {
    return (
      !!socketData && typeof socketData === 'object' && 'cardNumberEnding' in socketData && 'pocSessionId' in socketData
    );
  }

  private handlePocFailure(socketData: { data: unknown }) {
    if (!PocStore.isPocFailedData(socketData.data)) {
      logToNewRelic(
        'POC-FE - received unexpected data from socket on POC_FAILED event',
        `POC-FE - received unexpected data from socket on POC_FAILED event: ${JSON.stringify(socketData.data)}`,
        'error',
      );

      return;
    }

    this.setPocState({
      flow: 'failed',
      step: 'init',
      coreData: {
        retriedPocSessionId: socketData.data.pocSessionId,
      },
      commonData: {
        cardNumberEnding: socketData.data.cardNumberEnding,
        requestReason: socketData.data.requestReason,
        attemptNumber: socketData.data.attemptNumber,
      },
      extraData: { failureType: socketData.data.failureType },
    });
    this.hangingSessionUnseen = true;

    this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['PushPoc']);
  }

  private static isPocSuccessData(socketData: unknown): socketData is PocSuccessNotice {
    return (
      !!socketData &&
      typeof socketData === 'object' &&
      'cardNumberEnding' in socketData &&
      'pocSessionId' in socketData &&
      typeof socketData.pocSessionId === 'string' &&
      typeof socketData.cardNumberEnding === 'string'
    );
  }

  private handlePocSuccess(socketData: { data: unknown }) {
    if (!PocStore.isPocSuccessData(socketData.data)) {
      logToNewRelic(
        'POC-FE - received unexpected data from socket on POC_SUCCESS event',
        `POC-FE - received unexpected data from socket on POC_SUCCESS event: ${JSON.stringify(socketData.data)}`,
        'error',
      );

      return;
    }

    this.successNotice = socketData.data;
    this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['PushPoc']);
  }
}
